import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { authApi } from '@/api/auth';
import { clearCsrfToken } from '@/api/csrf';
import { consumeSignUpStarted, trackSignUpCompleted } from '@/lib/analytics';
import { getErrorStatus } from '@/lib/api-errors';
import { clearMobileDesktopRecommendationSeen } from '@/lib/mobile-desktop-recommendation';
import { resetUser } from '@/lib/posthog';
import { AuthBootstrapState, AuthStatus } from '@/types/auth';
import { User } from '@/types/user';

export const AUTH_BOOTSTRAP_QUERY_KEY = 'auth-bootstrap';

type AuthLoginOptions = {
    returnTo?: string;
    authorizationParams?: {
        connection?: string;
        login_hint?: string;
        screen_hint?: string;
        prompt?: string;
    };
};

const buildApiUrl = (path: string) => {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
        throw new Error('VITE_API_URL is required for authentication');
    }

    return `${apiUrl.replace(/\/$/, '')}${path}`;
};

/**
 * Returns true if the error is transient (network issue, server error)
 * and the query should be retried. Returns false for permanent auth
 * failures (401, 403, 404) which no amount of retrying will fix.
 */
const isTransientError = (error: unknown): boolean => {
    const status = getErrorStatus(error);

    if (status === undefined) return true;
    if (status >= 500) return true;
    if (status === 408 || status === 429) return true;

    return false;
};

interface AuthContextType {
    user: User | null;
    status: AuthStatus;
    isLoading: boolean;
    login: (options?: AuthLoginOptions) => Promise<void>;
    logout: () => void;
    isAuthenticated: boolean;
    /** Our own team's accounts, excluded from product analytics. */
    isInternal: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const TEST_AUTH_STORAGE_KEY = 'codex:e2e-auth';

const readTestAuthState = (): {
    isAuthenticated: boolean;
    user: User | null;
} => {
    if (typeof window === 'undefined') {
        return { isAuthenticated: false, user: null };
    }

    const rawValue = window.localStorage.getItem(TEST_AUTH_STORAGE_KEY);

    if (!rawValue) {
        return { isAuthenticated: false, user: null };
    }

    try {
        const parsed = JSON.parse(rawValue) as {
            isAuthenticated?: boolean;
            user?: User | null;
        };

        return {
            isAuthenticated: Boolean(parsed.isAuthenticated && parsed.user),
            user: parsed.user ?? null,
        };
    } catch {
        return { isAuthenticated: false, user: null };
    }
};

/**
 * Same-window signal that the E2E auth fixture changed.
 *
 * `storage` only fires in *other* tabs, so a write made by the app itself is
 * invisible to `TestAuthProvider`'s listener. Without this event a profile save
 * would update the query cache — which `TestAuthProvider` never reads — and
 * nothing on screen would move under Playwright.
 */
const TEST_AUTH_CHANGED_EVENT = 'laudopsi:test-auth-changed';

/**
 * Mirrors a profile patch into the E2E auth fixture. No-op outside test mode,
 * where the key is absent.
 */
const patchTestAuthState = (patch: Partial<User>) => {
    if (typeof window === 'undefined') return;

    const rawValue = window.localStorage.getItem(TEST_AUTH_STORAGE_KEY);
    if (!rawValue) return;

    try {
        const parsed = JSON.parse(rawValue) as {
            isAuthenticated?: boolean;
            user?: User | null;
        };

        window.localStorage.setItem(
            TEST_AUTH_STORAGE_KEY,
            JSON.stringify({
                ...parsed,
                user: parsed.user ? { ...parsed.user, ...patch } : parsed.user,
            }),
        );
        window.dispatchEvent(new Event(TEST_AUTH_CHANGED_EVENT));
    } catch {
        // A corrupt fixture is a test-setup problem, not a runtime one.
    }
};

/**
 * Pushes a just-saved profile change into the cached auth user, then refetches.
 *
 * `['auth-bootstrap']` is the whole app's single source for the psicóloga — the
 * laudo's cabeçalho, rodapé, assinatura and capa all render straight off it —
 * and it is `staleTime: Infinity` with `refetchOnWindowFocus` off globally, so
 * nothing brings it up to date on its own. Every mutation that edits the
 * profile has to come through here; otherwise the editor keeps showing the old
 * name until a hard reload, which reads as "saving is broken".
 *
 * The optional `patch` is applied immediately so the change is visible before
 * the bootstrap round-trip lands; the refetch that follows is the authority.
 */
export const useSyncAuthUser = () => {
    const queryClient = useQueryClient();

    return useCallback(
        async (patch?: Partial<User>) => {
            if (patch) {
                // `TestAuthProvider` holds the user in its own state and never
                // reads the query cache, so E2E needs the patch mirrored there.
                patchTestAuthState(patch);
                queryClient.setQueryData<AuthBootstrapState | null>(
                    [AUTH_BOOTSTRAP_QUERY_KEY],
                    (currentState) =>
                        currentState?.status === 'active'
                            ? { ...currentState, user: { ...currentState.user, ...patch } }
                            : currentState,
                );
            }

            await queryClient.invalidateQueries({ queryKey: [AUTH_BOOTSTRAP_QUERY_KEY] });
        },
        [queryClient],
    );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();

    const {
        data: authState,
        isLoading: isQueryLoading,
        error: queryError,
    } = useQuery<AuthBootstrapState | null>({
        queryKey: [AUTH_BOOTSTRAP_QUERY_KEY],
        queryFn: async () => {
            try {
                return await authApi.bootstrap();
            } catch (bootstrapError) {
                if (getErrorStatus(bootstrapError) !== 401) {
                    throw bootstrapError;
                }

                clearCsrfToken();

                return null;
            }
        },
        staleTime: Infinity,
        retry: (failureCount, error) => {
            if (failureCount >= 2) return false;
            return isTransientError(error);
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    });

    useEffect(() => {
        localStorage.removeItem('app_user');
        localStorage.removeItem('user');
    }, []);

    useEffect(() => {
        if (queryError) {
            console.error('Error connecting to backend authentication:', queryError);
            toast.error('Erro de Autenticação');
        }
    }, [queryError]);

    useEffect(() => {
        if (authState?.status === 'blocked_conflict') {
            toast.error(authState.reason);
        }
    }, [authState]);

    useEffect(() => {
        if (authState?.status !== 'pending_verification' && authState?.status !== 'active') {
            return;
        }

        if (consumeSignUpStarted()) {
            trackSignUpCompleted();
        }
    }, [authState?.status]);

    // Error reports were anonymous until now, which made it impossible to tell
    // whether a crash hit one user repeatedly or many users once. Only the id
    // is attached — never the e-mail.
    useEffect(() => {
        const userId = authState?.status === 'active' ? authState.user?.id : null;

        Sentry.setUser(userId ? { id: userId } : null);
    }, [authState]);

    const login = async (options?: AuthLoginOptions) => {
        const url = new URL(buildApiUrl('/auth/login'));
        url.searchParams.set('returnTo', options?.returnTo ?? '/');

        for (const [key, value] of Object.entries(options?.authorizationParams ?? {})) {
            if (value) {
                url.searchParams.set(key, value);
            }
        }

        // Re-arms the desktop recommendation: signing in overrides its
        // once-a-day budget, and this is when the nudge is most likely to land.
        clearMobileDesktopRecommendationSeen();

        window.location.assign(url.toString());
    };

    const logout = () => {
        clearCsrfToken();
        // Drops the analytics identity so the next person on this browser is
        // not attributed to the account that just signed out.
        resetUser();
        clearMobileDesktopRecommendationSeen();
        Sentry.setUser(null);
        queryClient.removeQueries({ queryKey: [AUTH_BOOTSTRAP_QUERY_KEY] });
        authApi
            .logout()
            .then(({ logoutUrl }) => {
                window.location.assign(logoutUrl);
            })
            .catch(() => {
                window.location.assign('/sign-in');
            });
    };

    const user = authState?.status === 'active' ? authState.user : null;
    const status = authState?.status ?? 'anonymous';
    const isInternal = authState?.status === 'active' && authState.is_internal === true;

    return (
        <AuthContext.Provider
            value={{
                user,
                status,
                isLoading: isQueryLoading,
                login,
                logout,
                isAuthenticated: status !== 'anonymous',
                isInternal,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const TestAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState(readTestAuthState);

    useEffect(() => {
        const syncState = () => {
            setState(readTestAuthState());
        };

        window.addEventListener('storage', syncState);
        window.addEventListener(TEST_AUTH_CHANGED_EVENT, syncState);
        return () => {
            window.removeEventListener('storage', syncState);
            window.removeEventListener(TEST_AUTH_CHANGED_EVENT, syncState);
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user: state.user,
                status: state.isAuthenticated ? 'active' : 'anonymous',
                isLoading: false,
                login: async () => undefined,
                logout: () => {
                    window.localStorage.removeItem(TEST_AUTH_STORAGE_KEY);
                    setState({ isAuthenticated: false, user: null });
                    clearCsrfToken();
                    clearMobileDesktopRecommendationSeen();
                },
                isAuthenticated: state.isAuthenticated,
                isInternal: false,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
